import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { PortalCollapsibleComponent } from '../portal-collapsible/portal-collapsible.component';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { AuthService, CustomerService, InsurenetInterfaceService } from '@core';
import { catchError, combineLatest, EMPTY, finalize, map } from 'rxjs';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { CtrlErrorMessageDirective } from '@co-3/ctrl-ui/forms';

@Component({
    selector: 'app-deactivate-account',
    imports: [PortalCollapsibleComponent, ReactiveFormsModule, CtrlIconComponent, CtrlErrorMessageDirective],
    templateUrl: './deactivate-account.component.html',
    styleUrl: './deactivate-account.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeactivateAccountComponent {
    private insurenetService = inject(InsurenetInterfaceService);
    private authService = inject(AuthService);
    private router = inject(Router);

    private user = toSignal(
        combineLatest([this.authService.auth$, inject(CustomerService).customer$]).pipe(
            map(([auth, customer]) => {
                if (auth && customer) {
                    return {
                        auth,
                        customer,
                    };
                }
                return null;
            }),
        ),
    );

    protected pending = signal(false);

    protected formGroup = inject(FormBuilder).group({
        password: ['', [Validators.required, Validators.minLength(5)]],
    });

    protected deactivateAccount(): void {
        const user = this.user();
        if (user === null) {
            throw new Error(
                'DeactivateAccountComponent: User data missing. Account deactivation is only valid when user is logged in.',
            );
        }
        this.pending.set(true);
        this.insurenetService
            .deactivateAccount(this.formGroup.controls.password.value, user)
            .pipe(
                catchError((err) => {
                    // Test if the error is because the wrong password was entered
                    if ('ErrorCode' in err && err.ErrorCode === 22) {
                        this.formGroup.controls.password.setErrors({ invalidPassword: true });
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.pending.set(false);
                }),
            )
            .subscribe(() => {
                this.authService.logout();
                this.router.navigate(['/login']);
            });
    }
}
